import { ThemeColors, RADIUS, TYPE } from "./theme";
import { requiresBackSide, computeScaleRatio, BackSideMode } from "../utils/documentRules";
import { CameraManager, CameraAccessError } from "../camera/CameraManager";
import { isRetryableCameraError } from "../camera/Permission";
import { DOC_CAMERA_IDEAL_WIDTH, DOC_CAMERA_IDEAL_HEIGHT, MAX_DOCUMENT_BYTES } from "../utils/constants";
import { assessQuality, assessFraming } from "../utils/ImageQuality";
import { icon, IconName } from "./icons";
import { renderSpinner } from "./spinner";
import { DOCUMENT_UPLOAD_ACCEPT, isAllowedDocumentType, isPdf, withDocumentType } from "../utils/documentFiles";

/**
 * Document capture UI — camera-based or file-upload for ID documents.
 *
 * Supports:
 *  - Camera capture (rear camera preferred on mobile)
 *  - File upload fallback
 *  - Preview with retake/confirm
 *  - Front and back document capture for documents requiring both sides
 */
export interface DocumentCaptureResult {
  front: Blob;
  back?: Blob; // Optional for documents that don't require back side
  // Whether the front image came from the live camera vs. a gallery/file
  // upload — callers use this to decide whether pre-warming the liveness
  // camera makes sense (reusing an already-open camera) or would surprise
  // a user who never touched their camera at all this session.
  viaCamera: boolean;
}

/**
 * Rejects a pending capture() when destroy() ends it on purpose: the user
 * went back, retook, or closed the widget. It is not a failed check.
 */
export class DocumentCaptureCancelledError extends Error {
  constructor() {
    super("Document capture was cancelled");
    this.name = "DocumentCaptureCancelledError";
  }
}

export class DocumentCapture {
  private root: HTMLDivElement | null = null;
  private videoEl: HTMLVideoElement | null = null;
  private videoWrapEl: HTMLDivElement | null = null;
  private cameraManager: CameraManager = new CameraManager();
  private orientationHandler: (() => void) | null = null;
  private resolveCapture: ((result: DocumentCaptureResult) => void) | null = null;
  private rejectCapture: ((err: Error) => void) | null = null;
  private frontViaCamera = false;
  private frontBlob: Blob | null = null;
  private backBlob: Blob | null = null;
  private currentSide: 'front' | 'back' = 'front';
  private requiresBackOverride: BackSideMode | undefined = undefined;
  private framingTimer: ReturnType<typeof setTimeout> | null = null;
  private framingStableCount = 0;
  private framingAutoTriggered = false;

  /**
   * Mount the document capture UI and return a promise that resolves
   * with the captured document image(s).
   */
  capture(
    container: HTMLElement,
    theme: ThemeColors,
    documentType: string,
    requiresBack?: BackSideMode
  ): Promise<DocumentCaptureResult> {
    return new Promise((resolve, reject) => {
      // Validate input parameters
      if (!container) {
        reject(new Error("Container element is required"));
        return;
      }
      if (!theme) {
        reject(new Error("Theme configuration is required"));
        return;
      }
      if (!documentType || typeof documentType !== 'string') {
        reject(new Error("Document type is required and must be a string"));
        return;
      }
      
      // Check if already capturing
      if (this.resolveCapture || this.rejectCapture) {
        reject(new Error("Document capture is already in progress"));
        return;
      }

      this.resolveCapture = resolve;
      this.rejectCapture = reject;
      this.frontBlob = null;
      this.backBlob = null;
      this.frontViaCamera = false;
      this.currentSide = 'front';
      this.requiresBackOverride = requiresBack;
      this.renderChoiceScreen(container, theme, documentType);
    });
  }

  destroy(): void {
    // Stop any ongoing camera streams
    this.stopCamera();
    
    // Clean up DOM elements
    if (this.root && this.root.parentElement) {
      this.root.parentElement.removeChild(this.root);
    }
    this.root = null;
    
    // Clean up any pending promises
    if (this.rejectCapture) {
      this.rejectCapture(new DocumentCaptureCancelledError());
      this.rejectCapture = null;
    }
    this.resolveCapture = null;
    
    // Clean up blob data
    this.frontBlob = null;
    this.backBlob = null;
    this.currentSide = 'front';
  }

  private getBackSideMode(documentType: string): BackSideMode {
    // Prefer the backend-provided flag; fall back to the shared heuristic only when absent.
    return this.requiresBackOverride !== undefined
      ? this.requiresBackOverride
      : requiresBackSide(documentType);
  }

  /** Whether a back-side step should be shown at all (required OR optional). */
  private shouldCaptureBackSide(documentType: string): boolean {
    return this.getBackSideMode(documentType) !== false;
  }

  /** Whether the back-side step, once shown, can be skipped by the user. */
  private isBackSideOptional(documentType: string): boolean {
    return this.getBackSideMode(documentType) === "optional";
  }

  private renderChoiceScreen(
    container: HTMLElement,
    theme: ThemeColors,
    documentType: string
  ): void {
    this.root = document.createElement("div");
    this.root.style.cssText = `
      display:flex;flex-direction:column;gap:0;padding:0;width:100%;
      font-family:system-ui,-apple-system,sans-serif;
    `;

    const docLabel  = this.getDocumentLabel(documentType);
    const requiresBack    = this.shouldCaptureBackSide(documentType);
    const backOptional    = this.isBackSideOptional(documentType);
    const isCapturingFront = this.currentSide === 'front';
    const sideLabel = requiresBack
      ? (isCapturingFront ? ' — Front side' : ' — Back side')
      : '';

    // ── TOP: compact header row ─────────────────────────────────────
    const headerRow = document.createElement("div");
    headerRow.style.cssText = `
      display:flex;align-items:center;gap:14px;
      padding:16px 20px 14px;
    `;

    const iconBox = document.createElement("div");
    iconBox.style.cssText = `
      width:48px;height:48px;border-radius:14px;flex-shrink:0;
      display:flex;align-items:center;justify-content:center;
      color:${theme.primary};
      background:${theme.primary}15;
      border:1.5px solid ${theme.primary}30;
    `;
    iconBox.innerHTML = icon(this._docIcon(documentType), 22);

    const titleBlock = document.createElement("div");
    const titleEl = document.createElement("div");
    titleEl.style.cssText = `color:${theme.text};font-size:${TYPE.headingSm}px;font-weight:700;line-height:1.3;`;
    titleEl.textContent = `Capture your ${docLabel}`;

    const subEl = document.createElement("div");
    subEl.style.cssText = `color:${theme.textSecondary};font-size:12px;margin-top:2px;`;
    subEl.textContent = requiresBack
      ? (isCapturingFront
          ? 'Step 1 of 2 — front side'
          : backOptional ? 'Step 2 of 2 — back side (optional)' : 'Step 2 of 2 — back side')
      : 'Choose how to provide your document';

    titleBlock.appendChild(titleEl);
    titleBlock.appendChild(subEl);
    headerRow.appendChild(iconBox);
    headerRow.appendChild(titleBlock);
    this.root.appendChild(headerRow);

    // ── MIDDLE: side-by-side action buttons ─────────────────────────
    const btnRow = document.createElement("div");
    btnRow.style.cssText = `
      display:grid;grid-template-columns:1fr 1fr;gap:10px;
      padding:0 20px 14px;
    `;

    // Camera button
    const cameraBtn = document.createElement("button");
    cameraBtn.style.cssText = `
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      gap:8px;padding:16px 12px;
      background:${theme.primary};color:#fff;
      border:none;border-radius:14px;cursor:pointer;
      font-size:13px;font-weight:600;font-family:inherit;
      transition:all 0.15s ease;min-height:80px;
      box-shadow:0 4px 14px ${theme.primary}30;
    `;
    cameraBtn.innerHTML = `<span style="display:inline-flex;">${icon("camera", 24)}</span><span>Take Photo</span><span style="font-size:10px;opacity:0.8;font-weight:500;">Use camera</span>`;
    cameraBtn.addEventListener("mouseenter", () => { cameraBtn.style.opacity = "0.9"; cameraBtn.style.transform = "translateY(-1px)"; });
    cameraBtn.addEventListener("mouseleave", () => { cameraBtn.style.opacity = "1";   cameraBtn.style.transform = "none"; });
    cameraBtn.addEventListener("click", () => {
      this.root!.innerHTML = "";
      this.renderCameraView(this.root!, theme, documentType);
    });

    // Upload button
    const uploadBtn = document.createElement("button");
    uploadBtn.style.cssText = `
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      gap:8px;padding:16px 12px;
      background:${theme.cardBg || theme.inputBg};color:${theme.text};
      border:2px solid ${theme.border};border-radius:14px;cursor:pointer;
      font-size:13px;font-weight:600;font-family:inherit;
      transition:all 0.15s ease;min-height:80px;
    `;
    uploadBtn.innerHTML = `<span style="display:inline-flex;">${icon("gallery", 24)}</span><span>From Gallery</span><span style="font-size:10px;color:${theme.textMuted || theme.textSecondary};font-weight:500;">Photo or PDF</span>`;
    uploadBtn.addEventListener("mouseenter", () => { uploadBtn.style.borderColor = theme.primary; uploadBtn.style.transform = "translateY(-1px)"; });
    uploadBtn.addEventListener("mouseleave", () => { uploadBtn.style.borderColor = theme.border;  uploadBtn.style.transform = "none"; });
    uploadBtn.addEventListener("click", () => { this.handleFileUpload(container, theme); });

    btnRow.appendChild(cameraBtn);
    btnRow.appendChild(uploadBtn);
    this.root.appendChild(btnRow);

    // ── BOTTOM: compact tips (no scroll) ────────────────────────────
    const tips = document.createElement("div");
    tips.style.cssText = `
      margin:0 20px 16px;padding:12px 14px;
      background:${theme.shimmer || theme.cardBg};
      border:1px solid ${theme.border};border-radius:${RADIUS.md}px;
    `;

    const tipsGrid = document.createElement("div");
    tipsGrid.style.cssText = `
      display:grid;grid-template-columns:1fr 1fr;gap:6px 12px;
    `;
    const tipItems = [
      "Lay document flat",
      "All 4 corners visible",
      "Good lighting, no glare",
      "All text clearly readable",
    ];
    tipItems.forEach((t) => {
      const row = document.createElement("div");
      row.style.cssText = `display:flex;align-items:center;gap:5px;font-size:11px;color:${theme.textSecondary};`;
      row.innerHTML = `<span style="color:${theme.primary};flex-shrink:0;display:inline-flex;">${icon("check", 12)}</span><span>${t}</span>`;
      tipsGrid.appendChild(row);
    });
    tips.appendChild(tipsGrid);
    this.root.appendChild(tips);

    // ── Back-side notice (shown only when requiresBack) ──────────────
    if (requiresBack && isCapturingFront) {
      const notice = document.createElement("div");
      notice.style.cssText = `
        margin:0 20px 16px;padding:10px 14px;
        background:${theme.primary}0D;
        border:1px solid ${theme.primary}25;border-radius:10px;
        display:flex;align-items:center;gap:8px;
        font-size:12px;color:${theme.textSecondary};line-height:1.5;
      `;
      // NIN specifically: spell out slip-vs-card rather than the generic
      // "if your document has one" — the NIN slip (paper printout) has no
      // usable back, while the NIN card (plastic card) does, and users
      // often don't know which one they're holding is called what.
      const isNin = documentType.toLowerCase().includes("nin");
      notice.innerHTML = backOptional
        ? isNin
          ? `<span style="font-size:16px;flex-shrink:0;">ℹ️</span><span>After the <strong style="color:${theme.text}">front</strong>: if you have the <strong style="color:${theme.text}">NIN card</strong> (plastic card), add the back too. If you have the <strong style="color:${theme.text}">NIN slip</strong> (paper printout), there's usually nothing to capture on the back — just skip it.</span>`
          : `<span style="font-size:16px;flex-shrink:0;">ℹ️</span><span>After the <strong style="color:${theme.text}">front</strong>, you can optionally add the <strong style="color:${theme.text}">back</strong> if your document has one.</span>`
        : `<span style="font-size:16px;flex-shrink:0;">ℹ️</span><span>You'll capture both the <strong style="color:${theme.text}">front</strong> and <strong style="color:${theme.text}">back</strong> of this document.</span>`;
      this.root.appendChild(notice);
    }

    // ── Skip button (back side only, and only when optional) ─────────
    if (requiresBack && !isCapturingFront && backOptional) {
      const skipBtn = document.createElement("button");
      skipBtn.style.cssText = `
        display:block;margin:0 20px 16px;width:calc(100% - 40px);
        padding:10px 12px;background:transparent;color:${theme.textSecondary};
        border:1.5px dashed ${theme.border};border-radius:10px;cursor:pointer;
        font-size:12.5px;font-weight:600;font-family:inherit;
      `;
      skipBtn.textContent = "Skip — my document has no back side";
      skipBtn.addEventListener("click", () => {
        this.handleCaptureComplete(null, container, theme, documentType, false, true);
      });
      this.root.appendChild(skipBtn);
    }

    // Hidden file input
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = DOCUMENT_UPLOAD_ACCEPT;
    fileInput.style.display = "none";
    fileInput.id = "sc-doc-file-input";
    fileInput.addEventListener("change", (e) => {
      const picked = (e.target as HTMLInputElement).files?.[0];
      if (picked) {
        const file = withDocumentType(picked);
        const err = this.validateFile(file);
        if (err) { this.showError(err); return; }
        if (isPdf(file)) {
          this.showPdfPreview(container, theme, file, documentType);
          return;
        }
        // Sent exactly as picked: validateFile already refused anything over
        // the upload limit, and nothing is ever re-encoded.
        this.handleFileUploadComplete(file, container, theme, documentType);
      }
    });
    this.root.appendChild(fileInput);

    container.appendChild(this.root);
  }

  /** Returns the icon name matching the document type. */
  private _docIcon(documentType: string): IconName {
    const t = documentType.toLowerCase();
    if (t.includes("passport"))                       return "passport";
    if (t.includes("driver") || t.includes("licen")) return "car";
    if (t.includes("voter") || t.includes("pvc"))    return "ballot";
    return "id-card";
  }

  private renderCameraView(
    container: HTMLElement,
    theme: ThemeColors,
    documentType: string
  ): void {
    // Dark cinematic chrome + corner-bracket guide — matches the liveness
    // scan screen's visual language (LivenessUI.ts) instead of the plain
    // rectangle outline this used to be, so the two camera experiences in
    // the flow read as one product.
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `
      position:relative;width:100%;max-width:420px;margin:0 auto;
      background:#0a0a0a;border-radius:${RADIUS.lg}px;overflow:hidden;
      box-shadow:0 8px 32px rgba(0,0,0,0.4);
    `;
    this.videoWrapEl = wrapper;

    this.videoEl = document.createElement("video");
    this.videoEl.autoplay = true;
    this.videoEl.playsInline = true;
    this.videoEl.muted = true;
    // Starts at 4:3 (typical document-shaped default); _fitToStream() below
    // corrects this to the stream's real aspect ratio once metadata loads,
    // instead of force-cropping every device's native ratio into one fixed box.
    this.videoEl.style.cssText = "width:100%;display:block;aspect-ratio:4/3;object-fit:cover;";
    wrapper.appendChild(this.videoEl);

    const vignette = document.createElement("div");
    vignette.style.cssText = `
      position:absolute;inset:0;pointer-events:none;
      box-shadow:inset 0 0 0 9999px rgba(0,0,0,0.38);
      -webkit-mask:radial-gradient(ellipse 46% 34% at 50% 50%, transparent 60%, #000 100%);
      mask:radial-gradient(ellipse 46% 34% at 50% 50%, transparent 60%, #000 100%);
    `;
    wrapper.appendChild(vignette);

    // Corner-bracket guide — same bracket-path language as LivenessUI's oval
    // guide, adapted to a rectangle since documents (unlike faces) aren't oval.
    const guideWrap = document.createElement("div");
    guideWrap.style.cssText = `
      position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
      width:84%;aspect-ratio:16/10;pointer-events:none;
    `;
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 160 100");
    svg.style.cssText = "width:100%;height:100%;overflow:visible;";
    const bracketGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    bracketGroup.setAttribute("stroke", theme.primary);
    bracketGroup.setAttribute("stroke-width", "3");
    bracketGroup.setAttribute("stroke-linecap", "round");
    bracketGroup.setAttribute("fill", "none");
    const bLen = 16;
    const bx0 = 4, bx1 = 156, by0 = 4, by1 = 96;
    const brackets = [
      `M${bx0},${by0 + bLen} L${bx0},${by0} L${bx0 + bLen},${by0}`,
      `M${bx1 - bLen},${by0} L${bx1},${by0} L${bx1},${by0 + bLen}`,
      `M${bx0},${by1 - bLen} L${bx0},${by1} L${bx0 + bLen},${by1}`,
      `M${bx1 - bLen},${by1} L${bx1},${by1} L${bx1},${by1 - bLen}`,
    ];
    for (const d of brackets) {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      bracketGroup.appendChild(path);
    }
    svg.appendChild(bracketGroup);
    guideWrap.appendChild(svg);
    wrapper.appendChild(guideWrap);

    // Header — gradient scrim + document label + live framing status
    const header = document.createElement("div");
    header.style.cssText = `
      position:absolute;top:0;left:0;right:0;
      background:linear-gradient(180deg,rgba(0,0,0,0.8) 0%,transparent 100%);
      padding:14px 16px 32px;text-align:center;
    `;
    header.innerHTML = `<div style="color:#fff;font-size:${TYPE.sm}px;font-weight:600;">Align your ${this.getDocumentLabel(documentType)} within the frame</div>`;
    const framingStatus = document.createElement("div");
    framingStatus.style.cssText = `
      margin-top:6px;font-size:${TYPE.xs}px;font-weight:600;
      color:rgba(255,255,255,0.7);transition:color 0.2s ease;
    `;
    framingStatus.textContent = "Fit the document inside the frame";
    header.appendChild(framingStatus);
    wrapper.appendChild(header);

    // Footer — gradient scrim + tips
    const footer = document.createElement("div");
    footer.style.cssText = `
      position:absolute;bottom:0;left:0;right:0;
      background:linear-gradient(0deg,rgba(0,0,0,0.7) 0%,transparent 100%);
      padding:28px 10px 18px;text-align:center;
    `;
    footer.innerHTML = `<div style="font-size:${TYPE.xs}px;color:rgba(255,255,255,0.55);">Good lighting · No glare · All 4 corners visible</div>`;
    wrapper.appendChild(footer);

    container.appendChild(wrapper);

    // Capture button
    const captureBtn = document.createElement("button");
    captureBtn.setAttribute("aria-label", "Capture photo");
    captureBtn.style.cssText = `
      display:block;margin:16px auto 0;width:64px;height:64px;
      border-radius:50%;border:4px solid ${theme.primary};
      background:transparent;cursor:pointer;position:relative;
      transition:all 0.2s ease;
    `;
    const inner = document.createElement("div");
    inner.style.cssText = `
      width:48px;height:48px;border-radius:50%;background:${theme.primary};
      position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
      transition:all 0.15s ease;
    `;
    captureBtn.appendChild(inner);
    captureBtn.addEventListener("mousedown", () => {
      inner.style.transform = "translate(-50%,-50%) scale(0.9)";
    });
    captureBtn.addEventListener("mouseup", () => {
      inner.style.transform = "translate(-50%,-50%) scale(1)";
    });
    captureBtn.addEventListener("click", () => {
      this.framingAutoTriggered = true; // manual tap always wins, skip any pending auto-trigger
      this.captureFrame(container, theme, documentType);
    });
    container.appendChild(captureBtn);

    // Open camera, then start the auto-capture framing loop once the stream
    // has real dimensions to sample against.
    this.openCamera().then(() => {
      if (this.videoEl) {
        this.videoEl.addEventListener(
          "loadedmetadata",
          () => this._startFramingLoop(container, theme, documentType, framingStatus),
          { once: true }
        );
      }
    });
  }

  /**
   * Matches the video box's aspect-ratio CSS to the stream's actual
   * negotiated dimensions instead of a single hardcoded ratio — a stream
   * that's natively 16:9 no longer gets force-cropped into a 4:3 box.
   * Clamped to a sane document-shaped range so a degenerate/unusual stream
   * can't produce a sliver-thin or absurdly tall box.
   */
  private _fitToStream(): void {
    if (!this.videoEl) return;
    const { videoWidth: w, videoHeight: h } = this.videoEl;
    if (!w || !h) return;
    const ratio = Math.min(Math.max(w / h, 1), 2); // clamp to [1:1, 2:1]
    this.videoEl.style.aspectRatio = `${ratio}`;
  }

  /**
   * Re-fits the video box when the device rotates mid-capture. Does not
   * re-request getUserMedia (disruptive/slow) — the stream's dimensions
   * don't change on rotation, only the viewport does, so re-reading the
   * already-known videoWidth/videoHeight is enough to keep the box sane.
   */
  private _watchOrientation(): void {
    this._unwatchOrientation();
    this.orientationHandler = () => this._fitToStream();
    window.addEventListener("orientationchange", this.orientationHandler);
    window.addEventListener("resize", this.orientationHandler);
  }

  private _unwatchOrientation(): void {
    if (this.orientationHandler) {
      window.removeEventListener("orientationchange", this.orientationHandler);
      window.removeEventListener("resize", this.orientationHandler);
      this.orientationHandler = null;
    }
  }

  /**
   * Auto-capture-on-stability — the same pattern the liveness module already
   * uses for face-lock (see liveness.ts's checkFace), applied to documents:
   * poll the raw video frame on an interval, score it against the guide box
   * with assessFraming() (see utils/ImageQuality.ts), and fire the shutter
   * automatically once several consecutive samples score as "framed". This
   * is industry-standard for document capture (Persona/Onfido/Jumio-style
   * SDKs all auto-capture rather than trust a manual tap against a static
   * guide overlay) — the guide box alone never blocked a badly cropped tap.
   *
   * The manual capture button still works throughout and always wins
   * (framingAutoTriggered guards against a race between the two).
   */
  private _startFramingLoop(
    container: HTMLElement,
    theme: ThemeColors,
    documentType: string,
    statusEl: HTMLElement
  ): void {
    if (!this.videoEl) return;
    this.framingStableCount = 0;
    this.framingAutoTriggered = false;

    const STABLE_REQUIRED = 3;     // consecutive good samples before firing
    const SAMPLE_INTERVAL_MS = 350; // cheap enough to run this often; not every animation frame
    const MAX_WAIT_MS = 12_000;    // give up auto-capture, leave the manual button as the only path
    const startTime = performance.now();

    // Guide box in fractional coordinates, matching the on-screen CSS guide
    // (width:84%, aspect-ratio:16/10, centered) — computed directly against
    // the video's own pixel dimensions so no CSS/object-fit mapping is needed.
    const boxWidthPct = 0.84;
    const boxAspect = 16 / 10;

    const tick = () => {
      if (!this.videoEl || this.framingAutoTriggered) return;
      if (performance.now() - startTime >= MAX_WAIT_MS) {
        statusEl.textContent = "Fit the document inside the frame";
        return; // stop auto-detecting; manual capture button remains available
      }

      const w = this.videoEl.videoWidth;
      const h = this.videoEl.videoHeight;
      if (!w || !h || this.videoEl.readyState < 2) {
        this.framingTimer = setTimeout(tick, SAMPLE_INTERVAL_MS);
        return;
      }

      try {
        const boxWidthPx = w * boxWidthPct;
        const boxHeightPx = boxWidthPx / boxAspect;
        const boxXPct = (1 - boxWidthPct) / 2;
        const boxHeightPct = boxHeightPx / h;
        const boxYPct = (1 - boxHeightPct) / 2;

        const canvas = document.createElement("canvas");
        // Downscaled sampling copy only — matches _checkPhotoQuality's own
        // reasoning for why this must not run against the full-resolution
        // frame on every tick.
        const scale = Math.min(1, 480 / w);
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          this.framingTimer = setTimeout(tick, SAMPLE_INTERVAL_MS);
          return;
        }
        ctx.drawImage(this.videoEl, 0, 0, canvas.width, canvas.height);
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const result = assessFraming(pixels, {
          x: boxXPct, y: boxYPct, width: boxWidthPct, height: boxHeightPct,
        });

        if (result.isFramed) {
          this.framingStableCount++;
          statusEl.style.color = theme.primary;
          statusEl.textContent = this.framingStableCount >= STABLE_REQUIRED
            ? "Capturing…"
            : "Hold steady…";
        } else {
          this.framingStableCount = 0;
          statusEl.style.color = "rgba(255,255,255,0.7)";
          statusEl.textContent = result.interiorVariance < 0.25
            ? "Move closer — fill the frame with your document"
            : "Align all 4 corners within the guide";
        }

        if (this.framingStableCount >= STABLE_REQUIRED) {
          this.framingAutoTriggered = true;
          this.captureFrame(container, theme, documentType);
          return;
        }
      } catch {
        // Advisory/auto-capture only — a scoring failure just falls back to
        // the manual capture button, never blocks the flow.
      }

      this.framingTimer = setTimeout(tick, SAMPLE_INTERVAL_MS);
    };

    this.framingTimer = setTimeout(tick, SAMPLE_INTERVAL_MS);
  }

  private _stopFramingLoop(): void {
    if (this.framingTimer !== null) {
      clearTimeout(this.framingTimer);
      this.framingTimer = null;
    }
  }

  private async openCamera(): Promise<void> {
    // Check browser compatibility
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.rejectCapture?.(new Error("Camera access is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari."));
      return;
    }

    const maxRetries = 3;

    // Retries only transient/negotiable failures (busy camera, unsupported
    // constraints, anything unrecognized) — a hard permission denial or "no
    // camera exists" can never be fixed by retrying, so those short-circuit
    // to an immediate error instead of burning ~6s of backoff first.
    const attemptCameraAccess = async (
      constraints: MediaStreamConstraints,
      retryCount = 0
    ): Promise<MediaStream> => {
      try {
        return await this.cameraManager.open(constraints);
      } catch (err: any) {
        console.warn(`Camera access attempt ${retryCount + 1} failed:`, err);

        const status = err instanceof CameraAccessError ? err.status : "unknown";
        if (isRetryableCameraError(status) && retryCount < maxRetries - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return attemptCameraAccess(constraints, retryCount + 1);
        }
        throw err;
      }
    };

    let stream: MediaStream;
    try {
      // Try rear camera first
      stream = await attemptCameraAccess({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: DOC_CAMERA_IDEAL_WIDTH },
          height: { ideal: DOC_CAMERA_IDEAL_HEIGHT },
        },
      });
    } catch (err: any) {
      console.warn("Rear camera failed, trying any camera:", err);

      // Fallback to any camera
      try {
        stream = await attemptCameraAccess({
          video: { width: { ideal: DOC_CAMERA_IDEAL_WIDTH } },
        });
      } catch (fallbackErr: any) {
        this.rejectCapture?.(new Error(fallbackErr?.message || "Camera access failed. Please check your camera permissions and try again."));
        return;
      }
    }

    // A capture that's already resolved (photo taken, camera intentionally
    // stopped) must not surface a stale interruption from the old stream.
    this.cameraManager.onStreamInterrupted(() => {
      if (this.resolveCapture || this.rejectCapture) {
        this.rejectCapture?.(new Error("Camera connection was lost. Please check your camera and try again."));
      }
    });

    if (this.videoEl) {
      this.videoEl.srcObject = stream;
      this.videoEl.addEventListener("loadedmetadata", () => this._fitToStream(), { once: true });
      this._watchOrientation();
    }
  }

  private captureFrame(container: HTMLElement, theme: ThemeColors, documentType: string): void {
    if (!this.videoEl) return;

    // Draw at the video's native resolution: the capture is uploaded as is,
    // never resized, so the OCR gets every detail the camera recorded.
    const canvas = document.createElement("canvas");
    canvas.width  = this.videoEl.videoWidth;
    canvas.height = this.videoEl.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(this.videoEl, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          this.stopCamera();
          this.showPreview(container, theme, blob, documentType, true);
        }
      },
      "image/jpeg",
      0.92
    );
  }

  private showPreview(
    container: HTMLElement,
    theme: ThemeColors,
    imageData: Blob | File,
    documentType: string,
    viaCamera: boolean
  ): void {
    if (this.root) this.root.innerHTML = "";
    else {
      this.root = document.createElement("div");
      container.appendChild(this.root);
    }

    this.root.style.cssText = "display:flex;flex-direction:column;align-items:center;gap:16px;";

    // Step badge — same pill treatment as SmartComplyFlow's document-confirm
    // screen (Step 2: check what we read), so this doesn't read as
    // unexplained repetition of that later screen — they check different
    // things (photo clarity here, OCR-extracted data there).
    const stepBadge = document.createElement("div");
    stepBadge.style.cssText = `
      display:inline-flex;align-items:center;gap:5px;
      padding:3px 11px;border-radius:${RADIUS.pill}px;
      font-size:10.5px;font-weight:600;letter-spacing:0.2px;
      color:${theme.primary};background:${theme.primary}15;border:1px solid ${theme.primary}30;
    `;
    stepBadge.textContent = "STEP 1 OF 2 · CHECK THE PHOTO";
    this.root.appendChild(stepBadge);

    // Preview image, wrapped so the rotate control can sit as a small
    // overlay button anchored to the photo itself — makes it visually
    // obvious it acts ON this image, instead of floating unexplained
    // between the copy and the quality notice.
    const imgWrap = document.createElement("div");
    imgWrap.style.cssText = "position:relative;width:100%;";

    const img = document.createElement("img");
    let currentUrl = URL.createObjectURL(imageData);
    img.src = currentUrl;
    img.style.cssText = `
      display:block;width:100%;max-height:300px;object-fit:contain;
      border-radius:${RADIUS.md}px;border:2px solid ${theme.border};
      background:${theme.inputBg};
    `;
    imgWrap.appendChild(img);

    // The blob actually sent on "Use Photo" — reassigned in place each time
    // rotate is tapped, so retake/use always act on the current orientation.
    let currentBlob: Blob = imageData;
    let isRotating = false;

    // Rotate control — fixes a document photographed/uploaded sideways or
    // upside-down. A sideways capture (or a scan/crop with no orientation
    // metadata) needs the user to say so.
    // Sits as a circular overlay button in the photo's corner (same pattern
    // as Google Photos/Drive) so its purpose — "turn this image" — reads
    // immediately from placement alone, with a text label for anyone who
    // still wants it spelled out.
    const rotateBtn = document.createElement("button");
    rotateBtn.type = "button";
    rotateBtn.setAttribute("aria-label", "Photo sideways or upside-down? Rotate it 90 degrees");
    rotateBtn.title = "Rotate 90°";
    rotateBtn.style.cssText = `
      position:absolute;bottom:10px;right:10px;
      display:flex;align-items:center;justify-content:center;gap:6px;
      padding:8px 14px;background:${theme.isDark ? "rgba(20,20,20,0.72)" : "rgba(255,255,255,0.92)"};
      backdrop-filter:blur(6px);color:${theme.text};border:1px solid ${theme.border};
      border-radius:${RADIUS.pill}px;cursor:pointer;
      box-shadow:0 2px 10px rgba(0,0,0,${theme.isDark ? "0.4" : "0.15"});
      font-size:12.5px;font-weight:600;font-family:inherit;line-height:1;
      transition:transform 0.15s ease,opacity 0.15s ease;
    `;
    rotateBtn.innerHTML = `<span style="display:inline-flex;">${icon("rotate", 15)}</span><span>Rotate</span>`;
    rotateBtn.addEventListener("mouseenter", () => { rotateBtn.style.transform = "translateY(-1px)"; });
    rotateBtn.addEventListener("mouseleave", () => { rotateBtn.style.transform = "translateY(0)"; });
    rotateBtn.addEventListener("click", async () => {
      if (isRotating) return;
      isRotating = true;
      rotateBtn.style.opacity = "0.5";
      rotateBtn.disabled = true;
      try {
        const rotated = await this._rotateImage(currentBlob, 90);
        currentBlob = rotated;
        URL.revokeObjectURL(currentUrl);
        currentUrl = URL.createObjectURL(rotated);
        img.src = currentUrl;
        this._checkPhotoQuality(currentBlob, qualityNotice);
      } catch {
        // Leave the current photo untouched — rotation is a convenience,
        // not something worth hard-failing the whole capture over.
      } finally {
        isRotating = false;
        rotateBtn.style.opacity = "1";
        rotateBtn.disabled = false;
      }
    });
    imgWrap.appendChild(rotateBtn);
    this.root.appendChild(imgWrap);

    const label = document.createElement("div");
    label.style.cssText = `color:${theme.text};font-size:${TYPE.base}px;font-weight:700;text-align:center;`;
    label.textContent = "Does the photo look clear?";
    this.root.appendChild(label);

    const sublabel = document.createElement("div");
    sublabel.style.cssText = `color:${theme.textMuted};font-size:${TYPE.xs}px;text-align:center;line-height:1.6;`;
    sublabel.textContent = "All text must be readable and all four corners must be visible. Sideways or upside-down? Use Rotate on the photo above.";
    this.root.appendChild(sublabel);

    // Quality/framing notice — advisory-only for both blur/glare/contrast
    // (assessQuality) AND framing (assessFraming). Framing used to hard-block
    // "Use Photo": real reports across three different ID photos each forced
    // a new threshold recalibration (a fixed brightness cutoff, then a
    // relative-to-interior-tone check) as the fix for one photo's false
    // positive became the next photo's false positive — a pixel-statistics
    // heuristic guessing "is this a document filling the frame" from edge
    // gradients and gray-value variance structurally cannot generalize across
    // every ID design, paper stock, and lighting condition a customer might
    // photograph. Ground-truth checks catch a genuinely cut-off document
    // downstream instead: OCR needs real readable text to extract fields at
    // all, and the registry re-check (sdk/services/iprs_recheck.py) fails
    // explicitly on a wrong/garbled ID number. The nudge text is still shown
    // (real signal, just not reliable enough to gate on) — the user can see
    // their own photo and judge for themselves.
    const qualityNotice = document.createElement("div");
    qualityNotice.style.cssText = `
      display:none;width:100%;padding:10px 14px;border-radius:${RADIUS.md}px;
      background:${theme.warning}15;border:1px solid ${theme.warning}35;
      color:${theme.text};font-size:12px;text-align:left;line-height:1.5;
    `;
    qualityNotice.setAttribute("role", "status");
    this.root.appendChild(qualityNotice);

    // Buttons
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:12px;width:100%;";

    const retakeBtn = this.createButton("Retake", theme, false);
    retakeBtn.style.flex = "1";
    retakeBtn.addEventListener("click", () => {
      URL.revokeObjectURL(currentUrl);
      this.root!.innerHTML = "";
      this.renderChoiceScreen(container, theme, documentType);
    });

    const useBtn = this.createButton("Use Photo ✓", theme, true);
    useBtn.style.flex = "1";
    useBtn.addEventListener("click", () => {
      URL.revokeObjectURL(currentUrl);
      useBtn.textContent = "Confirming photo…";
      useBtn.disabled = true;
      rotateBtn.disabled = true;
      // Sent exactly as captured; never re-encoded, so the OCR gets every
      // detail (shrinking a passport photo hid its machine-readable lines).
      if (currentBlob.size > MAX_DOCUMENT_BYTES) {
        useBtn.textContent = "Use Photo ✓";
        useBtn.disabled = false;
        rotateBtn.disabled = false;
        this.showError(this.tooLargeMessage());
        return;
      }
      this.handleCaptureComplete(currentBlob, container, theme, documentType, viaCamera);
    });

    btnRow.appendChild(retakeBtn);
    btnRow.appendChild(useBtn);
    this.root.appendChild(btnRow);

    this._checkPhotoQuality(currentBlob, qualityNotice);
  }

  /**
   * Runs the shared client-side quality heuristics — blur/glare/contrast/
   * brightness (assessQuality) and document framing/completeness
   * (assessFraming) — against the just-captured or just-uploaded photo, and
   * shows a plain-language nudge on the preview screen if either scores
   * poorly. This is the one chokepoint both the camera and manual-upload
   * paths funnel through (see showPreview), so it's the only place that
   * reliably catches a badly cropped document regardless of how the photo
   * arrived — the camera's own auto-capture framing loop only guards live
   * capture, and does nothing for an uploaded file or a manual shutter tap
   * fired before the loop ever samples.
   *
   * Framing here checks the WHOLE image (guideBoxPct = full frame), not a
   * guide-box region — there's no guide overlay for an arbitrary uploaded
   * photo, so "does the document fill most of the frame" is the correct
   * question, matching the same border-edge + interior-variance heuristic
   * the camera loop already uses.
   *
   * A framing failure shows the same nudge as blur/glare, but no longer
   * disables useBtn — see the comment above qualityNotice's creation in
   * showPreview for why. Fails open on decode error — this heuristic not
   * running at all must not lock the user out of a photo it never actually
   * scored.
   */
  private async _checkPhotoQuality(
    imageData: Blob | File,
    noticeEl: HTMLElement
  ): Promise<void> {
    try {
      // Document photos are captured at up to DOC_CAMERA_IDEAL_WIDTH/HEIGHT
      // (2000x1500) — scoring every pixel across four full-image passes in
      // assessQuality() would be genuinely slow on the low/mid-range phones
      // most likely to produce a poor-quality photo in the first place.
      // Downscale a scoring-only copy; the actual captured photo sent for
      // upload is completely unaffected by this.
      const canvas = await this._blobToCanvas(imageData, 640);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const framing = assessFraming(pixels, { x: 0, y: 0, width: 1, height: 1 });
      if (!framing.isFramed) {
        if (noticeEl.isConnected) {
          noticeEl.style.display = "block";
          noticeEl.textContent = framing.interiorVariance < 0.25
            ? "We couldn't detect a document in this photo — if the ID isn't clearly visible, consider retaking it."
            : "Part of the document may be cut off — if any corners are missing, consider retaking it.";
        }
        return; // framing issue is the more actionable/specific one — don't also show the quality nudge
      }

      const result = assessQuality(pixels);
      if (noticeEl.isConnected && !result.isValid) {
        noticeEl.style.display = "block";
        noticeEl.textContent =
          "This photo might be hard to read — " +
          (result.recommendations[0] || "try retaking it in good lighting") + ".";
      }
    } catch {
      // Fails open — no notice shown if scoring fails for any reason.
    }
  }

  /** Decodes to a canvas, optionally downscaled to maxDim on the long edge (never upscales). */
  private _blobToCanvas(source: Blob | File, maxDim?: number): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(source);
      const imgEl = document.createElement("img");
      imgEl.onload = () => {
        URL.revokeObjectURL(url);
        const { naturalWidth: w, naturalHeight: h } = imgEl;
        const ratio = maxDim ? computeScaleRatio(w, h, maxDim, maxDim) : 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(w * ratio);
        canvas.height = Math.round(h * ratio);
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas unavailable")); return; }
        ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
        resolve(canvas);
      };
      imgEl.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
      imgEl.src = url;
    });
  }

  /**
   * Rotate an image blob by a multiple of 90° and re-encode it — used by the
   * preview screen's rotate control for a document photographed or uploaded
   * sideways/upside-down. Orientation metadata can't help a document
   * whose actual pixel content is sideways (e.g. the phone itself
   * was held rotated relative to the document, or a pre-cropped/scanned
   * upload with no EXIF at all) — that needs the user to say so directly.
   */
  private _rotateImage(source: Blob, degrees: 90 | 180 | 270): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(source);
      const imgEl = document.createElement("img");
      imgEl.onload = () => {
        URL.revokeObjectURL(url);
        const { naturalWidth: w, naturalHeight: h } = imgEl;
        const swapped = degrees === 90 || degrees === 270;
        const canvas = document.createElement("canvas");
        canvas.width = swapped ? h : w;
        canvas.height = swapped ? w : h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas unavailable")); return; }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(imgEl, -w / 2, -h / 2);
        canvas.toBlob(
          (blob) => { blob ? resolve(blob) : reject(new Error("Rotation failed")); },
          "image/jpeg",
          0.95
        );
      };
      imgEl.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
      imgEl.src = url;
    });
  }

  private handleCaptureComplete(
    imageData: Blob | null,
    container: HTMLElement,
    theme: ThemeColors,
    documentType: string,
    viaCamera: boolean,
    skipped: boolean = false
  ): void {
    const requiresBack = this.shouldCaptureBackSide(documentType);
    const wasCapturingFront = this.currentSide === 'front';

    // Store the captured image (skip leaves backBlob unset — front-only submit)
    if (wasCapturingFront) {
      this.frontBlob = imageData;
      this.frontViaCamera = viaCamera;
    } else if (!skipped) {
      this.backBlob = imageData;
    }

    // If we need back side and haven't captured it yet, move to back side
    if (requiresBack && wasCapturingFront) {
      this.currentSide = 'back';
      this.root!.innerHTML = "";
      this.renderChoiceScreen(container, theme, documentType);
      return;
    }

    // All captures complete, resolve with result
    if (!this.frontBlob) {
      this.rejectCapture?.(new Error("No front image captured. Please try again."));
      return;
    }

    const result: DocumentCaptureResult = {
      front: this.frontBlob,
      back: this.backBlob || undefined,
      viaCamera: this.frontViaCamera,
    };
    this.resolveCapture?.(result);
  }

  /**
   * Confirm screen for an uploaded PDF. The photo preview can't be used: it
   * draws the file as an <img>, offers Rotate and runs an image-quality
   * check, none of which work on a PDF. The PDF is sent as-is; the backend
   * and OCR read it directly.
   */
  private showPdfPreview(
    container: HTMLElement,
    theme: ThemeColors,
    file: File,
    documentType: string
  ): void {
    if (this.root) this.root.innerHTML = "";
    else {
      this.root = document.createElement("div");
      container.appendChild(this.root);
    }
    this.root.style.cssText = "display:flex;flex-direction:column;align-items:center;gap:16px;";

    const card = document.createElement("div");
    card.style.cssText = `
      display:flex;align-items:center;gap:12px;width:100%;box-sizing:border-box;
      padding:16px;border-radius:${RADIUS.md}px;
      border:2px solid ${theme.border};background:${theme.inputBg};
    `;
    const fileIcon = document.createElement("span");
    fileIcon.style.cssText = `display:inline-flex;color:${theme.primary};flex-shrink:0;`;
    fileIcon.innerHTML = icon("document", 32);
    const details = document.createElement("div");
    details.style.cssText = "display:flex;flex-direction:column;gap:2px;min-width:0;";
    const name = document.createElement("div");
    name.style.cssText = `color:${theme.text};font-size:${TYPE.sm}px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
    name.textContent = file.name;
    const size = document.createElement("div");
    size.style.cssText = `color:${theme.textMuted};font-size:${TYPE.xs}px;`;
    size.textContent = `PDF · ${(file.size / (1024 * 1024)).toFixed(1)} MB`;
    details.appendChild(name);
    details.appendChild(size);
    card.appendChild(fileIcon);
    card.appendChild(details);
    this.root.appendChild(card);

    const label = document.createElement("div");
    label.style.cssText = `color:${theme.text};font-size:${TYPE.base}px;font-weight:700;text-align:center;`;
    label.textContent = "Is this the right document?";
    this.root.appendChild(label);

    const sublabel = document.createElement("div");
    sublabel.style.cssText = `color:${theme.textMuted};font-size:${TYPE.xs}px;text-align:center;line-height:1.6;`;
    sublabel.textContent = "Use a single-page PDF of the document, with all text readable.";
    this.root.appendChild(sublabel);

    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:12px;width:100%;";

    const chooseAnotherBtn = this.createButton("Choose another", theme, false);
    chooseAnotherBtn.style.flex = "1";
    chooseAnotherBtn.addEventListener("click", () => {
      this.root!.innerHTML = "";
      this.renderChoiceScreen(container, theme, documentType);
    });

    const useBtn = this.createButton("Use this file ✓", theme, true);
    useBtn.style.flex = "1";
    useBtn.addEventListener("click", () => {
      useBtn.textContent = "Confirming file…";
      useBtn.disabled = true;
      this.handleCaptureComplete(file, container, theme, documentType, false);
    });

    btnRow.appendChild(chooseAnotherBtn);
    btnRow.appendChild(useBtn);
    this.root.appendChild(btnRow);
  }

  private handleFileUploadComplete(
    imageData: Blob,
    container: HTMLElement,
    theme: ThemeColors,
    documentType: string
  ): void {
    this.showPreview(container, theme, imageData, documentType, false);
  }

  private validateFile(file: File): string | null {
    if (!isAllowedDocumentType(file)) {
      return "Invalid file type. Please upload a JPEG or PNG photo, or a PDF.";
    }

    // Anything under this is sent completely untouched (see proceedWithPhoto /
    // the file-input handler) — this is the real backend upload ceiling, not
    // a quality target, so it must match MAX_DOCUMENT_BYTES exactly.
    if (file.size > MAX_DOCUMENT_BYTES) {
      return this.tooLargeMessage();
    }

    // Check minimum file size (at least 1KB to avoid empty files)
    if (file.size < 1024) {
      return "File is too small. Please upload a valid photo or PDF.";
    }

    return null;
  }

  private tooLargeMessage(): string {
    return `File is too large. Please upload a file smaller than ${Math.round(MAX_DOCUMENT_BYTES / (1024 * 1024))}MB.`;
  }

  private handleFileUpload(container: HTMLElement, theme: ThemeColors): void {
    const input = this.root?.querySelector("#sc-doc-file-input") as HTMLInputElement;
    if (input) input.click();
  }

  private stopCamera(): void {
    this._stopFramingLoop();
    this._unwatchOrientation();
    this.cameraManager.stop();
    if (this.videoEl) {
      this.videoEl.srcObject = null;
      this.videoEl = null;
    }
    this.videoWrapEl = null;
  }

  private showLoadingState(message: string, theme: ThemeColors): void {
    if (!this.root) return;

    this.root.innerHTML = "";
    this.root.style.cssText = "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;padding:40px 20px;min-height:300px;";
    this.root.setAttribute("aria-live", "polite");
    this.root.setAttribute("role", "status");

    this.root.appendChild(renderSpinner(theme, { size: 80, icon: icon("document", 28) }));

    const loadingText = document.createElement("div");
    loadingText.style.cssText = `color:${theme.text};font-size:${TYPE.heading}px;font-weight:600;text-align:center;`;
    loadingText.textContent = message;
    this.root.appendChild(loadingText);
  }

  private showError(message: string): void {
    if (!this.root) return;

    const errorContainer = document.createElement("div");
    errorContainer.setAttribute("role", "alert");
    errorContainer.style.cssText = `
      display:flex;align-items:center;gap:12px;padding:16px;
      background:#ff444415;border:1px solid #ff444430;border-radius:${RADIUS.md}px;
      color:#ff4444;font-size:14px;margin-top:16px;animation:shake 0.5s ease-in-out;
    `;
    const errIcon = document.createElement("span");
    errIcon.style.fontSize = "18px";
    errIcon.textContent = "⚠️";
    const errText = document.createElement("span");
    errText.textContent = message;
    errorContainer.appendChild(errIcon);
    errorContainer.appendChild(errText);

    // Add shake animation
    if (!document.head.querySelector('style[data-shake-animation]')) {
      const style = document.createElement('style');
      style.setAttribute('data-shake-animation', 'true');
      style.textContent = `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
      `;
      document.head.appendChild(style);
    }
    
    this.root.appendChild(errorContainer);
    setTimeout(() => errorContainer.remove(), 5000);
  }

  private createButton(
    label: string,
    theme: ThemeColors,
    isPrimary: boolean
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.textContent = label;
    btn.style.cssText = `
      padding:16px 24px;border-radius:${RADIUS.md}px;font-size:16px;font-weight:600;
      cursor:pointer;transition:all 0.2s ease;border:none;width:100%;
      min-height:52px;touch-action:manipulation;-webkit-tap-highlight-color:transparent;
      ${
        isPrimary
          ? `background:${theme.primary};color:#fff;box-shadow:0 4px 12px ${theme.primary}30;`
          : `background:${theme.inputBg};color:${theme.text};border:2px solid ${theme.border};`
      }
    `;
    btn.addEventListener("mouseenter", () => {
      btn.style.transform = "translateY(-1px)";
      if (isPrimary) btn.style.background = theme.primaryHover;
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.transform = "translateY(0)";
      if (isPrimary) btn.style.background = theme.primary;
    });
    return btn;
  }

  private getDocumentLabel(type: string): string {
    const t = type.toLowerCase();
    if (t.includes("passport"))                              return "passport";
    if (t.includes("driver") || t.includes("licen"))        return "driver's license";
    if (t.includes("voter") || t.includes("pvc"))           return "voter's card";
    // NIN covers both the NIN slip and the physical NIN card — "document"
    // rather than "card" so slip users don't get told to align a "card"
    // they don't have.
    if (t.includes("nin"))                                  return "NIN document";
    if (t.includes("national"))                              return "national ID card";
    if (t.includes("bvn"))                                  return "BVN document";
    return "identity document";
  }
}
